import { trackEvent } from '../firebase';
import {
  getAssignedVariant,
  getLandingPageVariant,
  LANDING_HERO_EXPERIMENT_ID,
  LANDING_PAGE_EXPERIMENT_ID,
} from './experiments';

const SESSION_PREFIX = 'logami.analytics.once.session.';
const USER_PREFIX = 'logami.analytics.once.user.';
const ADS_SIGNUP_COMPLETE_SEND_TO = 'AW-18013129599/wHWNCMGnpowcEP-Wqo1D';

function getExperimentContextParams() {
  const landingPageVariant = getLandingPageVariant();
  const landingHeroVariant = getAssignedVariant(LANDING_HERO_EXPERIMENT_ID);

  if (landingPageVariant) {
    return {
      experiment_id: LANDING_PAGE_EXPERIMENT_ID,
      experiment_variant: landingPageVariant,
    };
  }

  if (!landingHeroVariant) {
    return {};
  }

  return {
    experiment_id: LANDING_HERO_EXPERIMENT_ID,
    experiment_variant: landingHeroVariant,
  };
}

function getSessionStore() {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch (_) {
    return null;
  }
}

function getLocalStore() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch (_) {
    return null;
  }
}

function markOnce(storage, key) {
  if (!storage) return true;
  const existing = storage.getItem(key);
  if (existing === '1') return false;
  storage.setItem(key, '1');
  return true;
}

function oncePerSession(key) {
  return markOnce(getSessionStore(), `${SESSION_PREFIX}${key}`);
}

function oncePerUser(userId, key) {
  if (!userId) return false;
  return markOnce(getLocalStore(), `${USER_PREFIX}${userId}.${key}`);
}

export function trackConversionEvent(eventName, params = {}) {
  if (!eventName) return;
  trackEvent(eventName, {
    ...getExperimentContextParams(),
    ...params,
  });
}

function trackGoogleAdsConversion(sendTo, params = {}) {
  if (!sendTo || typeof window === 'undefined') return;
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', 'conversion', {
    send_to: sendTo,
    ...params,
  });
}

export function trackAuthView({ mode = 'login', source = 'auth_page' } = {}) {
  if (!oncePerSession(`auth_view.${mode}`)) return;
  trackConversionEvent('auth_view', { mode, source });
  if (mode === 'signup') {
    trackGoogleAdsConversion(ADS_SIGNUP_COMPLETE_SEND_TO);
  }
}

export function trackSignupStart({ method = 'email' } = {}) {
  if (!oncePerSession(`signup_start.${method}`)) return;
  trackConversionEvent('signup_start', { method });
}

export function trackSignupComplete({ method = 'email', userId } = {}) {
  if (userId && !oncePerUser(userId, 'signup_complete')) return;
  trackConversionEvent('signup_complete', { method });
}

export function trackOnboardingStepCompleted({ stepName, stepNumber, stepIndex } = {}) {
  if (!stepName) return;
  if (!oncePerSession(`onboarding_step_completed.${stepName}`)) return;
  const resolvedStepNumber = stepNumber ?? stepIndex ?? null;

  trackConversionEvent('onboarding_step_completed', {
    step_name: stepName,
    step_number: resolvedStepNumber,
  });
}

export function trackOnboardingComplete({ userId, pricingPlanId, planId, examProvider, level, examLevel, tookDiagnostic } = {}) {
  if (userId && !oncePerUser(userId, 'onboarding_complete')) return;
  trackConversionEvent('onboarding_complete', {
    pricing_plan_id: pricingPlanId || planId || 'free',
    exam_provider: examProvider || null,
    exam_level: level || examLevel || null,
    took_diagnostic: Boolean(tookDiagnostic),
  });
}

export function trackPricingView({ source = 'pricing_page' } = {}) {
  if (!oncePerSession(`pricing_view.${source}`)) return;
  trackConversionEvent('pricing_view', { source });
}

export function trackPlanSelected({ planId, planType, source = 'pricing_page', sourceContext, context } = {}) {
  const resolvedPlanId = planId || planType;
  if (!resolvedPlanId) return;
  const resolvedSource = sourceContext || context || source;

  trackConversionEvent('plan_selected', {
    plan_id: resolvedPlanId,
    plan_type: resolvedPlanId,
    source_context: resolvedSource,
  });

  // Fire a separate event for paid plan selections so we can measure
  // real paid intent in GA4 without filtering on plan_id parameter.
  const isFree = resolvedPlanId === 'free' || resolvedPlanId === 'basic' || resolvedPlanId === 'starter';
  if (!isFree) {
    trackConversionEvent('paid_plan_selected', {
      plan_id: resolvedPlanId,
      source_context: resolvedSource,
    });
  }
}

export function trackCheckoutStart({ planId, planType, amount, fromOnboarding = false, requiresAuth = false, source = 'checkout' } = {}) {
  const resolvedPlanId = planId || planType;
  if (!resolvedPlanId) return;
  if (!oncePerSession(`checkout_start.${resolvedPlanId}.${source}`)) return;
  trackConversionEvent('checkout_start', {
    plan_id: resolvedPlanId,
    amount: typeof amount === 'number' ? amount : null,
    from_onboarding: Boolean(fromOnboarding),
    requires_auth: Boolean(requiresAuth),
    source,
  });
}

export function trackVerificationGateShown({ planId } = {}) {
  if (!planId) return;
  if (!oncePerSession(`verification_gate_shown.${planId}`)) return;
  trackConversionEvent('verification_gate_shown', { plan_id: planId });
}

export function trackVerificationGateResolved({ planId } = {}) {
  if (!planId) return;
  if (!oncePerSession(`verification_gate_resolved.${planId}`)) return;
  trackConversionEvent('verification_gate_resolved', { plan_id: planId });
}

export function trackPaymentIntentCreated({ planId, paymentIntentId, amount, discountCode } = {}) {
  if (!planId) return;
  const dedupeKey = paymentIntentId || `${planId}.${amount || 'na'}`;
  if (!oncePerSession(`payment_intent_created.${dedupeKey}`)) return;
  trackConversionEvent('payment_intent_created', {
    plan_id: planId,
    payment_intent_id: paymentIntentId || null,
    amount: typeof amount === 'number' ? amount : null,
    discount_code: discountCode || null,
  });
}

export function trackPurchase({ transactionId, planId, planName, value, currency = 'EUR', source = 'checkout' } = {}) {
  if (!planId || typeof value !== 'number') return;

  const dedupeKey = transactionId || `${planId}.${value}.${source}`;
  if (!oncePerSession(`purchase.${dedupeKey}`)) return;

  const payload = {
    value,
    currency,
    source,
    items: [
      {
        item_id: planId,
        item_name: planName || planId,
        price: value,
        quantity: 1,
      },
    ],
  };

  if (transactionId) {
    payload.transaction_id = transactionId;
  }

  trackConversionEvent('purchase', payload);
}

export function trackPaywallShown({ context = 'unknown' } = {}) {
  trackConversionEvent('paywall_shown', { context });
}

export function trackFirstStudyAction({ userId, actionType, activityId } = {}) {
  if (!userId || !actionType) return;
  if (!oncePerUser(userId, 'first_study_action')) return;
  trackConversionEvent('first_study_action', {
    action_type: actionType,
    activity_id: activityId || null,
  });
}

export function trackReturnMilestones(user) {
  const userId = user?.uid;
  const createdAtRaw = user?.metadata?.creationTime;
  if (!userId || !createdAtRaw) return;

  const createdAt = new Date(createdAtRaw);
  if (Number.isNaN(createdAt.getTime())) return;

  const elapsedMs = Date.now() - createdAt.getTime();
  if (elapsedMs < 0) return;

  const daysSinceSignup = Math.floor(elapsedMs / (24 * 60 * 60 * 1000));

  if (daysSinceSignup >= 2 && oncePerUser(userId, 'day_2_return')) {
    trackConversionEvent('day_2_return', { days_since_signup: daysSinceSignup });
  }

  if (daysSinceSignup >= 7 && oncePerUser(userId, 'day_7_return')) {
    trackConversionEvent('day_7_return', { days_since_signup: daysSinceSignup });
  }
}
